import React from 'react';

// Fungsi untuk mendapatkan base specs berdasarkan nama produk
const getBaseSpecs = (prodName) => {
  const specs = {
    // VPS Paket
    'Nano SC': {
      ssd: '15GB',
      ram: '0.5GB',
      cpu: '1 Core'
    },
    'SC1': {
      ssd: '25GB',
      ram: '1GB',
      cpu: '1 Core'
    },
    'SC2': {
      ssd: '50GB',
      ram: '2GB',
      cpu: '1 Core'
    },
    'SC4': {
      ssd: '80GB',
      ram: '4GB',
      cpu: '2 Core'
    },
    'SC8': {
      ssd: '160GB',
      ram: '8GB',
      cpu: '4 Core'
    },
    'SC16': {
      ssd: '320GB',
      ram: '16GB',
      cpu: '8 Core'
    },
    'SC32': {
      ssd: '640GB',
      ram: '32GB',
      cpu: '16 Core'
    },
    'SC64': {
      ssd: '1280GB',
      ram: '64GB',
      cpu: '32 Core'
    },
    // VPS Custom
    'Custom VPS': {
      ssd: '20GB',
      ram: '2GB',
      cpu: '2 Core'
    }
  };

  // Extract nama produk dari productName (hilangkan App Catalog dan server location)
  let cleanProdName = prodName;
  if (prodName && prodName.includes('+')) {
    cleanProdName = prodName.split('+')[0].trim();
  }
  
  // Hapus server location jika ada (dalam kurung)
  if (cleanProdName && cleanProdName.includes('(')) {
    cleanProdName = cleanProdName.split('(')[0].trim();
  }
  
  // Jangan split berdasarkan spasi untuk nama seperti "Nano SC", "SC1", dll
  // Cek langsung apakah ada di specs
  if (cleanProdName && specs[cleanProdName]) {
    return specs[cleanProdName];
  }

  return specs['Custom VPS'];
};

const VpsSpecsDisplay = ({ productName, configs = {}, showConfigSelector = false, onConfigChange, configOptions, termin }) => {
  // Dapatkan base specs berdasarkan nama produk
  const baseSpecs = getBaseSpecs(productName);
  
  // Hitung total specs dengan config tambahan
  // Untuk VPS Custom, gunakan configs, untuk VPS Paket gunakan base specs
  const isCustomVPS = productName === 'Custom VPS' || (productName && productName.includes('Custom'));
  
  const totalSpecs = isCustomVPS ? {
    ssd: `${configs.additional_space || 20}GB`,
    ram: `${configs.additional_ram || 2}GB`,
    cpu: `${configs.additional_cores || 2} Core`
  } : {
    ssd: baseSpecs.ssd,
    ram: baseSpecs.ram,
    cpu: baseSpecs.cpu
  };

  // Hitung tambahan yang ditampilkan (hanya untuk VPS Custom)
  const additionalSpecs = isCustomVPS ? {
    ssd: Math.max(0, (configs.additional_space || 20) - 20),
    ram: Math.max(0, (configs.additional_ram || 2) - 2),
    cpu: Math.max(0, (configs.additional_cores || 2) - 2)
  } : {
    ssd: 0,
    ram: 0,
    cpu: 0
  };

  return (
    <div className="vps-specs-display">
      <h6 className="fw-bold mb-3">Spesifikasi VPS</h6>
      
      {/* Specs Product dengan Layout Responsif - Grid di desktop, vertikal di mobile */}
      <div className="row g-3">
        {/* CPU Core */}
        <div className="col-12 col-md-4">
          <div className="d-flex align-items-center justify-content-between p-3 h-100 bg-light rounded">
            <div className="d-flex align-items-center flex-grow-1">
              <div className="me-2">
                <i className="fas fa-microchip text-warning" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}></i>
              </div>
              <div className="flex-grow-1">
                <div className="fw-bold text-dark" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1rem)' }}>CPU Core</div>
                <small className="text-muted d-block d-md-inline" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)' }}>Processing Power</small>
              </div>
            </div>
            <div className="text-end ms-2">
              {additionalSpecs.cpu > 0 && (
                <small className="text-success d-block mb-1" style={{ fontSize: 'clamp(0.7rem, 1vw, 0.8rem)' }}>
                  +{additionalSpecs.cpu} Core
                </small>
              )}
              <div className="fw-bold text-warning" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>{totalSpecs.cpu}</div>
            </div>
          </div>
        </div>

        {/* RAM */}
        <div className="col-12 col-md-4">
          <div className="d-flex align-items-center justify-content-between p-3 h-100 bg-light rounded">
            <div className="d-flex align-items-center flex-grow-1">
              <div className="me-2">
                <i className="fas fa-memory text-success" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}></i>
              </div>
              <div className="flex-grow-1">
                <div className="fw-bold text-dark" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1rem)' }}>Dedicated RAM</div>
                <small className="text-muted d-block d-md-inline" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)' }}>Memory Server</small>
              </div>
            </div>
            <div className="text-end ms-2">
              {additionalSpecs.ram > 0 && (
                <small className="text-success d-block mb-1" style={{ fontSize: 'clamp(0.7rem, 1vw, 0.8rem)' }}>
                  +{additionalSpecs.ram}GB
                </small>
              )}
              <div className="fw-bold text-success" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>{totalSpecs.ram}</div>
            </div>
          </div>
        </div>

        {/* Storage (SSD) */}
        <div className="col-12 col-md-4">
          <div className="d-flex align-items-center justify-content-between p-3 h-100 bg-light rounded">
            <div className="d-flex align-items-center flex-grow-1">
              <div className="me-2">
                <i className="fas fa-hdd text-primary" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}></i>
              </div>
              <div className="flex-grow-1">
                <div className="fw-bold text-dark" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1rem)' }}>Storage (SSD)</div>
                <small className="text-muted d-block d-md-inline" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)' }}>Raid 10 SSD</small>
              </div>
            </div>
            <div className="text-end ms-2">
              {additionalSpecs.ssd > 0 && (
                <small className="text-success d-block mb-1" style={{ fontSize: 'clamp(0.7rem, 1vw, 0.8rem)' }}>
                  +{additionalSpecs.ssd}GB
                </small>
              )}
              <div className="fw-bold text-primary" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>{totalSpecs.ssd}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VpsSpecsDisplay;
